/**
 * QRScanPage — full-screen, mobile-first page for a user holding the
 * `scan_qr` additive capability. Opens the device camera and delegates
 * all validation + attendance state to the existing backend endpoints.
 *
 * Defense in depth (iter-58):
 *   1. Requires `scan_qr` in the user's roles array.
 *   2. Requires the active branch to have `attendance.attendance_method === "qr"`.
 *   3. Requires a specific branch (never "all").
 * If any check fails we render an "Access Not Available" card. The backend
 * `/api/qr/checkin/submit` re-validates every field so a direct URL entry
 * cannot bypass this.
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Camera, Prohibit } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { ScanQRPanel } from "@/components/attendance/QRCheckinDialog";

export default function QRScanPage() {
  const navigate = useNavigate();
  const { activeBranch, branches, user } = useAuth();
  const branch = (branches || []).find(b => b.id === activeBranch);
  const hasCap = (user?.roles || []).includes("scan_qr")
                  || (user?.roles || []).includes("owner")
                  || (user?.roles || []).includes("admin");
  const branchIsQr = branch?.attendance?.attendance_method === "qr";
  const accessDenied = !hasCap || activeBranch === "all" || !branch || !branchIsQr;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col bg-background" data-testid="qr-scan-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card">
        <Button
          variant="ghost" size="icon" className="h-8 w-8"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="qr-scan-back"
        >
          <ArrowLeft size={18} />
        </Button>
        <div className="flex items-center gap-2">
          <Camera size={20} weight="duotone" />
          <div className="text-sm font-semibold">Scan QR</div>
        </div>
      </div>

      <div className="flex-1 p-4">
        {accessDenied ? (
          <div className="max-w-sm w-full mx-auto text-center space-y-3 border border-dashed rounded-lg p-6 mt-6" data-testid="qr-access-denied">
            <div className="mx-auto h-11 w-11 rounded-full bg-muted flex items-center justify-center">
              <Prohibit size={20} weight="duotone" className="text-muted-foreground" />
            </div>
            <div className="font-display font-semibold text-lg">Access Not Available</div>
            <p className="text-sm text-muted-foreground">
              {!hasCap
                ? "Your account doesn't have the Scan QR permission. Ask an admin to grant it under Settings → Users."
                : activeBranch === "all"
                ? "Pick a specific branch from the top bar."
                : !branch
                ? "The selected branch could not be found."
                : "This branch is not on QR attendance. Ask an admin to enable QR under Settings → Branches → Edit."}
            </p>
          </div>
        ) : (
          <div className="w-full max-w-md mx-auto">
            <ScanQRPanel onClose={() => navigate(-1)} />
          </div>
        )}
      </div>
    </div>
  );
}
